import type {
  CanvasPoint,
  CircleHole,
  HoleGripKind,
  PolylineLikeShape,
  Shape,
  ShapeGripDescriptor,
} from './types';
const clonePoint = (point: CanvasPoint): CanvasPoint => ({ x: point.x, y: point.y });

const cloneCircleHole = (hole: CircleHole): CircleHole => ({
  center: clonePoint(hole.center),
  radius: hole.radius,
});

const cloneShape = (shape: Shape): Shape => {
  if (shape.kind === 'circle' || shape.kind === 'arc') {
    return {
      ...shape,
      center: clonePoint(shape.center),
    };
  }

  return {
    ...shape,
    points: [...shape.points],
    holes: shape.holes?.map((hole) => [...hole]),
    circleHoles: shape.circleHoles?.map(cloneCircleHole),
  };
};

const getArcStartPoint = (shape: Extract<Shape, { kind: 'arc' }>) => ({
  x: shape.center.x + Math.cos(shape.startAngle) * shape.radius,
  y: shape.center.y + Math.sin(shape.startAngle) * shape.radius,
});

const getArcEndPoint = (shape: Extract<Shape, { kind: 'arc' }>) => ({
  x: shape.center.x + Math.cos(shape.endAngle) * shape.radius,
  y: shape.center.y + Math.sin(shape.endAngle) * shape.radius,
});

const getArcSweep = (shape: Extract<Shape, { kind: 'arc' }>) =>
  ((shape.endAngle - shape.startAngle) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);

const getArcMidPoint = (shape: Extract<Shape, { kind: 'arc' }>) => {
  const sweep = getArcSweep(shape);
  const midpointAngle = shape.startAngle + (sweep === 0 ? Math.PI : sweep / 2);
  return {
    x: shape.center.x + Math.cos(midpointAngle) * shape.radius,
    y: shape.center.y + Math.sin(midpointAngle) * shape.radius,
  };
};

const getPolylineVertices = (points: number[]) => {
  const vertices: CanvasPoint[] = [];

  for (let index = 0; index < points.length - 1; index += 2) {
    const x = points[index];
    const y = points[index + 1];
    if (x === undefined || y === undefined) continue;
    const nextVertex = { x, y };
    const previousVertex = vertices[vertices.length - 1];

    if (
      previousVertex &&
      Math.abs(previousVertex.x - nextVertex.x) <= 0.0001 &&
      Math.abs(previousVertex.y - nextVertex.y) <= 0.0001
    ) {
      continue;
    }

    vertices.push(nextVertex);
  }

  return vertices;
};

const getRenderableClosedVertices = (points: number[], closed: boolean) => {
  const vertices = getPolylineVertices(points);
  if (!closed || vertices.length < 2) return vertices;

  const first = vertices[0];
  const last = vertices[vertices.length - 1];
  if (!first || !last) return vertices;
  if (Math.abs(first.x - last.x) <= 0.0001 && Math.abs(first.y - last.y) <= 0.0001) {
    return vertices.slice(0, -1);
  }

  return vertices;
};

const addInsertGripsForVertices = (
  grips: ShapeGripDescriptor[],
  vertices: CanvasPoint[],
  closed: boolean,
  options: {
    holeIndex?: number;
    holeGripKind?: HoleGripKind;
  } = {},
) => {
  if (vertices.length < 2) return;

  const segmentCount = closed ? vertices.length : vertices.length - 1;
  for (let index = 0; index < segmentCount; index += 1) {
    const start = vertices[index];
    const end = vertices[(index + 1) % vertices.length];
    if (!start || !end) continue;

    grips.push({
      point: {
        x: (start.x + end.x) / 2,
        y: (start.y + end.y) / 2,
      },
      vertexIndex: index * 2,
      gripKind: 'insert',
      segmentStartVertexIndex: index * 2,
      holeIndex: options.holeIndex,
      holeGripKind: options.holeGripKind,
    });
  }
};

export const getShapeGripPoints = (shape: Shape): ShapeGripDescriptor[] => {
  if (shape.kind === 'circle') {
    return [
      {
        point: { x: shape.center.x + shape.radius, y: shape.center.y },
        vertexIndex: 0,
        gripKind: 'vertex',
      },
    ];
  }

  if (shape.kind === 'arc') {
    return [
      {
        point: getArcStartPoint(shape),
        vertexIndex: 0,
        gripKind: 'vertex',
      },
      {
        point: getArcMidPoint(shape),
        vertexIndex: 1,
        gripKind: 'vertex',
      },
      {
        point: getArcEndPoint(shape),
        vertexIndex: 2,
        gripKind: 'vertex',
      },
    ];
  }

  const exteriorVertices = getRenderableClosedVertices(shape.points, shape.closed);
  const grips: ShapeGripDescriptor[] = [];
  exteriorVertices.forEach((point, index) => {
    grips.push({
      point,
      vertexIndex: index * 2,
      gripKind: 'vertex',
    });
  });

  if (shape.kind !== 'rectangle') {
    addInsertGripsForVertices(grips, exteriorVertices, shape.closed);
  }

  for (const [holeIndex, hole] of (shape.holes ?? []).entries()) {
    const holeVertices = getPolylineVertices(hole);
    grips.push(
      ...holeVertices.map((point, index) => ({
        point,
        vertexIndex: index * 2,
        gripKind: 'vertex' as const,
        holeIndex,
        holeGripKind: 'polygon-vertex' as const,
      })),
    );
    addInsertGripsForVertices(grips, holeVertices, true, {
      holeIndex,
      holeGripKind: 'polygon-vertex',
    });
  }

  for (const [holeIndex, hole] of (shape.circleHoles ?? []).entries()) {
    grips.push(
      {
        point: clonePoint(hole.center),
        vertexIndex: 0,
        gripKind: 'vertex',
        holeIndex,
        holeGripKind: 'circle-center',
      },
      {
        point: { x: hole.center.x + hole.radius, y: hole.center.y },
        vertexIndex: 0,
        gripKind: 'vertex',
        holeIndex,
        holeGripKind: 'circle-radius',
      },
    );
  }

  return grips;
};

export const getGripAnchorPoint = (
  shape: Shape,
  vertexIndex: number,
  holeIndex?: number,
  holeGripKind?: HoleGripKind,
): CanvasPoint => {
  if (shape.kind === 'circle') {
    return shape.center;
  }

  if (shape.kind === 'arc') {
    return shape.center;
  }

  if (holeIndex != null && holeGripKind && holeGripKind !== 'polygon-vertex') {
    const circleHole = shape.circleHoles?.[holeIndex];
    if (circleHole) {
      return circleHole.center;
    }
  }

  const contour = holeIndex == null ? shape.points : shape.holes?.[holeIndex];
  const source = contour ?? shape.points;
  const x = source[vertexIndex];
  const y = source[vertexIndex + 1];
  if (x === undefined || y === undefined) {
    throw new Error(`Invalid vertex index ${vertexIndex} for shape grip anchor`);
  }
  return { x, y };
};

export const getGripCurrentPoint = (
  shape: Shape,
  vertexIndex: number,
  holeIndex?: number,
  holeGripKind?: HoleGripKind,
): CanvasPoint => {
  if (shape.kind === 'circle') {
    return { x: shape.center.x + shape.radius, y: shape.center.y };
  }

  if (shape.kind === 'arc') {
    if (vertexIndex === 0) return getArcStartPoint(shape);
    if (vertexIndex === 1) return getArcMidPoint(shape);
    return getArcEndPoint(shape);
  }

  if (holeIndex != null && holeGripKind === 'circle-center') {
    const hole = shape.circleHoles?.[holeIndex];
    if (hole) return clonePoint(hole.center);
  }

  if (holeIndex != null && holeGripKind === 'circle-radius') {
    const hole = shape.circleHoles?.[holeIndex];
    if (hole) {
      return { x: hole.center.x + hole.radius, y: hole.center.y };
    }
  }

  const contour = holeIndex == null ? shape.points : shape.holes?.[holeIndex];
  const source = contour ?? shape.points;
  const x = source[vertexIndex];
  const y = source[vertexIndex + 1];
  if (x === undefined || y === undefined) {
    throw new Error(`Invalid vertex index ${vertexIndex} for shape grip point`);
  }
  return { x, y };
};

export const buildShapeWithUpdatedGrip = (
  shape: Shape,
  vertexIndex: number,
  nextPoint: CanvasPoint,
  getDistance: (pointA: CanvasPoint, pointB: CanvasPoint) => number,
  linkedVertexIndices?: number[],
  holeIndex?: number,
  holeGripKind?: HoleGripKind,
): Shape => {
  const nextShape = cloneShape(shape);

  if (nextShape.kind === 'circle') {
    nextShape.radius = Math.max(1, getDistance(nextShape.center, nextPoint));
    return nextShape;
  }

  if (nextShape.kind === 'arc') {
    const angle = Math.atan2(nextPoint.y - nextShape.center.y, nextPoint.x - nextShape.center.x);
    if (vertexIndex === 0) {
      nextShape.startAngle = angle;
    } else if (vertexIndex === 2) {
      nextShape.endAngle = angle;
    } else {
      nextShape.radius = Math.max(1, getDistance(nextShape.center, nextPoint));
    }
    return nextShape;
  }

  if (holeIndex != null && holeGripKind === 'circle-center') {
    const circleHole = nextShape.circleHoles?.[holeIndex];
    if (circleHole) {
      circleHole.center = clonePoint(nextPoint);
    }
    return nextShape;
  }

  if (holeIndex != null && holeGripKind === 'circle-radius') {
    const circleHole = nextShape.circleHoles?.[holeIndex];
    if (circleHole) {
      circleHole.radius = Math.max(1, getDistance(circleHole.center, nextPoint));
    }
    return nextShape;
  }

  if (holeIndex != null) {
    const targetHole = nextShape.holes?.[holeIndex];
    if (targetHole) {
      targetHole[vertexIndex] = nextPoint.x;
      targetHole[vertexIndex + 1] = nextPoint.y;
    }
    return nextShape;
  }

  if (nextShape.kind === 'rectangle') {
    return nextShape;
  }

  nextShape.points[vertexIndex] = nextPoint.x;
  nextShape.points[vertexIndex + 1] = nextPoint.y;
  linkedVertexIndices?.forEach((linkedVertexIndex) => {
    nextShape.points[linkedVertexIndex] = nextPoint.x;
    nextShape.points[linkedVertexIndex + 1] = nextPoint.y;
  });
  return nextShape;
};

export const isHoleGripUpdateValid = (
  shape: Shape,
  vertexIndex: number,
  nextPoint: CanvasPoint,
  getDistance: (pointA: CanvasPoint, pointB: CanvasPoint) => number,
  isHoleInsideShape: (
    shape: PolylineLikeShape,
    hole: number[] | CircleHole,
    options?: {
      ignorePolygonHoleIndex?: number;
      ignoreCircleHoleIndex?: number;
    },
  ) => boolean,
  linkedVertexIndices?: number[],
  holeIndex?: number,
  holeGripKind?: HoleGripKind,
) => {
  if (shape.kind === 'circle' || shape.kind === 'arc' || holeIndex == null) {
    return true;
  }

  const nextShape = buildShapeWithUpdatedGrip(
    shape,
    vertexIndex,
    nextPoint,
    getDistance,
    linkedVertexIndices,
    holeIndex,
    holeGripKind,
  );
  if (nextShape.kind === 'circle' || nextShape.kind === 'arc') {
    return false;
  }

  if (holeGripKind === 'circle-center' || holeGripKind === 'circle-radius') {
    const hole = nextShape.circleHoles?.[holeIndex];
    return !!hole && isHoleInsideShape(nextShape, hole, { ignoreCircleHoleIndex: holeIndex });
  }

  const hole = nextShape.holes?.[holeIndex];
  return !!hole && isHoleInsideShape(nextShape, hole, { ignorePolygonHoleIndex: holeIndex });
};
